import React, { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { compose } from 'recompose';
import { Button, Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { PageTitle } from '../blocks';
import Snackbar from '@/components/Snackbar';
import { AUTO_ASSIGN } from '@/constants/routes';
import { profilePath } from './ProfilesGrid/utils';
import { Context, ContextProvider } from './store';
import { usePanelSubject } from '../panel/hooks';
import SubregionsColumn from './SubregionsColumn';
import MapColumn from './MapColumn';
import DeliveryProfileModal from './DeliveryProfileModal';
import DiscardChangesModal from '../panel/DiscardChangesModal';
import PageNotice from './PageNotice';

const AutoAssign = () => {
    const { state, loading, callbacks } = useContext(Context);
    const navigate = useNavigate();
    // null when closed; `{ profile }` when open, with this page's profile for Edit and null for Create.
    const [profileModal, setProfileModal] = useState(null);

    // "+ Add Subregion" opens an empty panel and persists nothing until Save. One
    // panel, one subject, one place that moves it, one guard in front of every transition.
    const panel = usePanelSubject({
        selectedSubregionId: state.selectedSubregion?.subregion_id || null,
        callbacks,
    });

    const toProfiles = () => navigate(AUTO_ASSIGN);

    // A new profile opens on its own page, through the same guard as switching profiles.
    const { upsertDeliveryProfile } = callbacks;
    const { request } = panel;
    const upsertProfile = useCallback(
        (values, options) =>
            upsertDeliveryProfile(values, options).then((res) => {
                const saved = res?.data?.result;
                if (saved?.delivery_profile_id && !values.delivery_profile_id) {
                    request({ kind: 'leave', proceed: () => navigate(profilePath(saved.delivery_profile_id)) });
                }
                return res;
            }),
        [upsertDeliveryProfile, request, navigate]
    );

    if (state.scope.kind === 'corp' && !state.scope.org_id) {
        return (
            <PageNotice title="Auto Assign Regions">
                <p>Your account is not associated with an organization — contact support.</p>
            </PageNotice>
        );
    }

    if (state.profileNotFound) {
        return (
            <PageNotice title="Delivery profile not found">
                <p>This delivery profile doesn't exist, or it belongs to another organization.</p>
                <Button variant="outlined" onClick={toProfiles}>
                    All profiles
                </Button>
            </PageNotice>
        );
    }

    if (state.profileLoadFailed) {
        return (
            <PageNotice title="Couldn't load this delivery profile">
                <p>Something went wrong loading it. Refresh the page to try again.</p>
                <Button variant="outlined" onClick={toProfiles}>
                    All profiles
                </Button>
            </PageNotice>
        );
    }

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                background: ${colors.white.primary};
                flex-wrap: nowrap;
            `}
        >
            <Snackbar
                open={!!state.notification.message}
                {...state.notification}
                handleClose={() => callbacks.setNotification({})}
            />
            <DeliveryProfileModal
                open={!!profileModal}
                onClose={() => setProfileModal(null)}
                deliveryProfile={profileModal?.profile || null}
                callbacks={{ upsertDeliveryProfile: upsertProfile }}
            />
            <DiscardChangesModal {...panel.discardPrompt} />
            <Grid
                container
                css={css`
                    padding: 23px 45px;
                    border-bottom: 1px solid ${colors.greys[0]};
                    align-items: center;
                    justify-content: space-between;
                    flex: 0;
                `}
            >
                <PageTitle>Auto Assign Regions</PageTitle>
            </Grid>
            <Grid
                container
                css={css`
                    flex: 1;
                    flex-basis: 0;
                    min-height: 0;
                    flex-wrap: nowrap;
                `}
            >
                <SubregionsColumn
                    deliveryProfileId={state.deliveryProfileId}
                    onBack={() => panel.request({ kind: 'leave', proceed: toProfiles })}
                    onSelectProfile={(id) => {
                        if (id === state.deliveryProfileId) return;
                        panel.request({ kind: 'leave', proceed: () => navigate(profilePath(id)) });
                    }}
                    onEditProfile={() => setProfileModal({ profile: state.deliveryProfile })}
                    onCreateProfile={() => setProfileModal({ profile: null })}
                    onAddSubregion={() => panel.request({ kind: 'new' })}
                    onSelectSubregion={(id) => panel.request({ kind: 'select', id })}
                    state={state}
                    loading={loading}
                    callbacks={callbacks}
                />
                <MapColumn
                    state={state}
                    loading={loading}
                    callbacks={callbacks}
                    carriersForLeg={state.carriersForLeg}
                    creating={panel.creating}
                    onDirtyChange={panel.setDirty}
                    onClose={() => panel.request({ kind: 'close' })}
                    onSaved={(saved) => {
                        panel.request({ kind: 'saved', id: saved?.subregion_id || null });
                        callbacks.setNotification({ severity: 'success', message: 'Subregion saved' });
                    }}
                />
            </Grid>
        </Grid>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withContext)(AutoAssign);
